import { useCallback, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import {
  uploadImageWithOptionalCompression,
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
} from '../utils/imageUpload';

/** antd message z-index is below CompressionConfirmModal; wait for modal to unmount. */
const SUCCESS_TOAST_AFTER_MODAL_MS = 280;

/**
 * Cloudinary upload with optional CompressionConfirmModal (same flow as sheet images).
 * @param {{ zIndex?: number, onBusyChange?: (phase?: string) => void, uploadSuccessMessage?: string }} [options]
 */
export function useCompressionImageUpload({
  zIndex = 5000,
  onBusyChange,
  uploadSuccessMessage,
} = {}) {
  const [open, setOpen] = useState(false);
  const [fileSize, setFileSize] = useState(0);
  const [choiceButtonsDisabled, setChoiceButtonsDisabled] = useState(false);
  const [uploadingChoice, setUploadingChoice] = useState(null);
  const compressPendingRef = useRef(null);

  const resetModal = useCallback(() => {
    setOpen(false);
    setFileSize(0);
    setChoiceButtonsDisabled(false);
    setUploadingChoice(null);
    compressPendingRef.current = null;
  }, []);

  const finishChoice = useCallback((compress) => {
    const p = compressPendingRef.current;
    if (!p) return;
    compressPendingRef.current = null;
    setUploadingChoice(compress ? 'compress' : 'original');
    setChoiceButtonsDisabled(true);
    p.resolve(compress);
  }, []);

  const cancelModal = useCallback(() => {
    const p = compressPendingRef.current;
    if (p) {
      compressPendingRef.current = null;
      p.reject(new Error('cancelled'));
    }
    resetModal();
  }, [resetModal]);

  const uploadImageFile = useCallback(
    async (file) => {
      if (!file || !file.type?.startsWith?.('image/')) {
        throw new Error('Please select an image file');
      }
      const useCompressModal = file.size > DEFAULT_COMPRESS_THRESHOLD_BYTES;
      try {
        const url = await uploadImageWithOptionalCompression(file, {
          thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
          promptCompress: useCompressModal
            ? () =>
                new Promise((resolve, reject) => {
                  compressPendingRef.current = { resolve, reject, file };
                  setChoiceButtonsDisabled(false);
                  setUploadingChoice(null);
                  setFileSize(file.size);
                  setOpen(true);
                })
            : undefined,
          onBusyChange,
        });
        if (uploadSuccessMessage) {
          const show = () => message.success(uploadSuccessMessage);
          if (useCompressModal) {
            window.setTimeout(show, SUCCESS_TOAST_AFTER_MODAL_MS);
          } else {
            show();
          }
        }
        return url;
      } finally {
        resetModal();
      }
    },
    [onBusyChange, resetModal, uploadSuccessMessage]
  );

  const compressionModalProps = useMemo(
    () => ({
      open,
      fileSize,
      uploadingChoice,
      choiceButtonsDisabled,
      onCompress: () => finishChoice(true),
      onUploadOriginal: () => finishChoice(false),
      onCancel: cancelModal,
      zIndex,
    }),
    [open, fileSize, uploadingChoice, choiceButtonsDisabled, finishChoice, cancelModal, zIndex]
  );

  return { uploadImageFile, compressionModalProps };
}
